import { jwtRoles } from "../../redux/utils/authRoles";

/** @typedef {{ username?: string | null; decodedToken?: Record<string, unknown> | null }} AuthState */
/** @typedef {{ empId?: number | string; displayName?: string }} EmployeeOption */
/**
 * Login / employee id (same value the user enters at sign-in).
 * @param {AuthState | null | undefined} auth
 */
export function getLoggedInEmployeeId(auth) {
  const fromUsername = String(auth?.username ?? "").trim();
  if (fromUsername) {
    return fromUsername;
  }
  const decoded = auth?.decodedToken;
  const fromToken = String(
    decoded?.userName ??
      decoded?.username ??
      decoded?.UserName ??
      decoded?.preferred_username ??
      decoded?.sub ??
      "",
  ).trim();
  return fromToken;
}

/**
 * First name + last name when available (JWT, employee directory, or `name` claim).
 * @param {AuthState | null | undefined} auth
 * @param {EmployeeOption[]} [employees]
 */
export function getLoggedInEmployeeFullName(auth, employees = []) {
  const given = String(auth?.decodedToken?.given_name ?? "").trim();
  const family = String(auth?.decodedToken?.family_name ?? "").trim();
  if (given || family) {
    return [given, family].filter(Boolean).join(" ");
  }

  const empId = getLoggedInEmployeeId(auth);
  if (empId && Array.isArray(employees) && employees.length > 0) {
    const match = employees.find((e) => String(e.empId) === empId);
    const fromList = match?.displayName?.trim();
    if (fromList) {
      return fromList;
    }
  }

  return String(auth?.decodedToken?.name ?? "").trim();
}

/**
 * Header label: "First Last (12345)".
 * @param {AuthState | null | undefined} auth
 * @param {EmployeeOption[]} [employees]
 */
export function formatAuthUserHeaderLabel(auth, employees = []) {
  const id = getLoggedInEmployeeId(auth);
  const name = getLoggedInEmployeeFullName(auth, employees);
  if (name && id) {
    return `${name} (${id})`;
  }
  if (name) {
    return name;
  }
  if (id) {
    return id;
  }
  return "User";
}

/**
 * Billing role name from JWT claims, or non-default `roles` when no billing role is set.
 * @param {AuthState | null | undefined} auth
 */
export function getLoggedInUserRoleLabel(auth) {
  const billingRoleName = String(auth?.decodedToken?.billingRoleName ?? "").trim();
  if (billingRoleName) {
    return billingRoleName;
  }

  const billingRoleDescription = String(auth?.decodedToken?.billingRoleDescription ?? "").trim();
  if (billingRoleDescription) {
    return billingRoleDescription;
  }

  const roles = jwtRoles(auth?.decodedToken).filter((role) => role.toUpperCase() !== "USER");
  if (roles.length === 0) {
    return "";
  }
  return roles.join(", ");
}

/**
 * Resolves the header role line from profile API, JWT, role directory, or assignable employees.
 * Prefers {@code role_name} over {@code role_description} when both are available.
 * @param {AuthState | null | undefined} auth
 * @param {{
 *   authProfile?: { billingRoleDescription?: string | null; billingRoleName?: string | null } | null;
 *   rolePrivileges?: Array<{ roleName?: string | null; roleDescription?: string | null; employees?: Array<{ empId?: number | string }> }>;
 *   assignableEmployees?: Array<{ empId?: number | string; currentRoleName?: string | null; currentRoleDescription?: string | null }>;
 * }} [options]
 */
export function resolveLoggedInUserRoleLabel(auth, options = {}) {
  const { authProfile, rolePrivileges = [], assignableEmployees = [] } = options;

  const fromProfileName = String(authProfile?.billingRoleName ?? "").trim();
  if (fromProfileName) {
    return fromProfileName;
  }

  const fromProfileDesc = String(authProfile?.billingRoleDescription ?? "").trim();
  if (fromProfileDesc) {
    return fromProfileDesc;
  }

  const fromJwt = getLoggedInUserRoleLabel(auth);
  if (fromJwt) {
    return fromJwt;
  }

  const empId = getLoggedInEmployeeId(auth);
  if (empId && Array.isArray(rolePrivileges) && rolePrivileges.length > 0) {
    for (const role of rolePrivileges) {
      const employees = role?.employees ?? [];
      if (!employees.some((employee) => String(employee?.empId) === empId)) {
        continue;
      }
      const roleName = String(role?.roleName ?? "").trim();
      if (roleName) {
        return roleName;
      }
      const roleDescription = String(role?.roleDescription ?? "").trim();
      if (roleDescription) {
        return roleDescription;
      }
    }
  }

  if (empId && Array.isArray(assignableEmployees) && assignableEmployees.length > 0) {
    const match = assignableEmployees.find((employee) => String(employee?.empId) === empId);
    const roleName = String(match?.currentRoleName ?? "").trim();
    if (roleName) {
      return roleName;
    }
    const roleDescription = String(match?.currentRoleDescription ?? "").trim();
    if (roleDescription) {
      return roleDescription;
    }
  }

  return "";
}

/**
 * Resolves the billing role description for header tooltips and similar UI.
 * @param {AuthState | null | undefined} auth
 * @param {{
 *   authProfile?: { billingRoleDescription?: string | null; billingRoleName?: string | null } | null;
 *   rolePrivileges?: Array<{ roleName?: string | null; roleDescription?: string | null; employees?: Array<{ empId?: number | string }> }>;
 *   assignableEmployees?: Array<{ empId?: number | string; currentRoleName?: string | null; currentRoleDescription?: string | null }>;
 * }} [options]
 */
export function resolveLoggedInUserRoleDescription(auth, options = {}) {
  const { authProfile, rolePrivileges = [], assignableEmployees = [] } = options;

  const fromProfileDesc = String(authProfile?.billingRoleDescription ?? "").trim();
  if (fromProfileDesc) {
    return fromProfileDesc;
  }

  const fromJwt = String(auth?.decodedToken?.billingRoleDescription ?? "").trim();
  if (fromJwt) {
    return fromJwt;
  }

  const empId = getLoggedInEmployeeId(auth);
  if (empId && Array.isArray(rolePrivileges) && rolePrivileges.length > 0) {
    for (const role of rolePrivileges) {
      const employees = role?.employees ?? [];
      if (!employees.some((employee) => String(employee?.empId) === empId)) {
        continue;
      }
      const roleDescription = String(role?.roleDescription ?? "").trim();
      if (roleDescription) {
        return roleDescription;
      }
    }
  }

  if (empId && Array.isArray(assignableEmployees) && assignableEmployees.length > 0) {
    const match = assignableEmployees.find((employee) => String(employee?.empId) === empId);
    const roleDescription = String(match?.currentRoleDescription ?? "").trim();
    if (roleDescription) {
      return roleDescription;
    }
  }

  return "";
}
